import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  FlaskConical,
  AlertTriangle, 
  Stethoscope, 
  TrendingUp,
  TrendingDown,
  Minus,
  Clock,
  Loader2,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Activity,
  Heart,
  Lightbulb,
  AlertCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import { Skeleton } from "../../components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../components/ui/collapsible";
import { toast } from "sonner";
import axios from "axios";
import { LabsLayout } from "../../components/LabsLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/labs`;

export default function LabReportAnalysisPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    testValues: true,
    abnormal: true,
    conditions: true,
    actions: true,
    lifestyle: false,
    fullAnalysis: false
  });

  useEffect(() => {
    fetchReport();
  }, [id]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/lab-reports/${id}`, { withCredentials: true });
      setReport(response.data);
    } catch (error) {
      console.error("Error fetching report:", error);
      toast.error("Failed to load report");
      navigate("/labs/lab-reports");
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const copyToClipboard = async () => {
    if (!report) return;
    
    const testValuesText = report.test_values?.map(t => 
      `${t.test_name}: ${t.value} ${t.unit} (Ref: ${t.reference_range}) - ${t.status.toUpperCase()}`
    ).join('\n') || '';

    const text = `
Laboratory Report Analysis
==========================
Report Type: ${report.report_type}
Category: ${report.report_category}
Date: ${new Date(report.created_at).toLocaleDateString()}

TEST VALUES:
${testValuesText}

ABNORMAL FINDINGS:
${report.abnormal_findings?.map((f, i) => `${i + 1}. ${f}`).join('\n') || 'None'}

CLINICAL INTERPRETATION:
${report.clinical_interpretation}

POSSIBLE CONDITIONS:
${report.possible_conditions?.map((c, i) => `${i + 1}. ${c}`).join('\n') || 'None'}

RECOMMENDED ACTIONS:
${report.recommended_actions?.map((a, i) => `${i + 1}. ${a}`).join('\n') || 'None'}

URGENT FLAGS:
${report.urgent_flags?.map((f, i) => `${i + 1}. ${f}`).join('\n') || 'None'}

LIFESTYLE RECOMMENDATIONS:
${report.lifestyle_recommendations?.map((l, i) => `${i + 1}. ${l}`).join('\n') || 'None'}

FULL ANALYSIS:
${report.full_analysis}
    `.trim();

    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Report copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'high':
        return <TrendingUp className="w-4 h-4 text-red-400" />;
      case 'low':
        return <TrendingDown className="w-4 h-4 text-amber-400" />;
      case 'critical':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default:
        return <Minus className="w-4 h-4 text-emerald-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'high':
        return 'bg-red-500/10 text-red-400 border-red-500/20';
      case 'low':
        return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      case 'critical':
        return 'bg-red-600/20 text-red-300 border-red-500/30';
      default:
        return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
    }
  };

  if (loading) {
    return (
      <LabsLayout>
        <LoadingSkeleton />
      </LabsLayout>
    );
  }

  if (!report) {
    return (
      <LabsLayout>
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-[#09090B]">
          <div className="text-center">
            <p className="text-zinc-400 mb-4">Report not found</p>
            <Button onClick={() => navigate("/labs/lab-reports")} data-testid="go-lab-reports-btn">
              Go Back
            </Button>
          </div>
        </div>
      </LabsLayout>
    );
  }

  return (
    <LabsLayout>
      <div className="min-h-[calc(100vh-4rem)] py-8 bg-[#09090B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/labs/lab-reports")}
                data-testid="back-btn"
                className="text-zinc-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold font-manrope text-white">
                  Lab Report Analysis
                </h1>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    {report.report_type || "Laboratory Report"}
                  </Badge>
                  <Badge variant="secondary" className="bg-zinc-800 text-zinc-300">
                    {report.report_category || "General"}
                  </Badge>
                  <span className="text-sm text-zinc-500 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {new Date(report.created_at).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
            <Button
              onClick={copyToClipboard}
              variant="outline"
              data-testid="copy-lab-report-btn"
              className="bg-zinc-900 border-zinc-700 hover:bg-zinc-800 text-zinc-300"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2 text-emerald-500" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Report
                </>
              )}
            </Button>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column - Main Analysis */}
            <div className="lg:col-span-8 space-y-6">
              {/* Clinical Interpretation Card */}
              <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
                <div className="bg-emerald-600/10 border-b border-emerald-600/20 p-4">
                  <div className="flex items-center gap-3">
                    <Stethoscope className="w-6 h-6 text-emerald-500" />
                    <h2 className="text-xl font-semibold font-manrope text-white">
                      Clinical Interpretation
                    </h2>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-lg text-zinc-200 leading-relaxed" data-testid="clinical-interpretation">
                    {report.clinical_interpretation || "No interpretation available"}
                  </p>
                </CardContent>
              </Card>

              {/* Test Values */}
              <CollapsibleSection
                icon={FlaskConical}
                title="Test Values"
                count={report.test_values?.length}
                isOpen={expandedSections.testValues}
                onToggle={() => toggleSection("testValues")}
                testId="test-values-section"
                accentColor="emerald"
              >
                <div className="space-y-2">
                  {report.test_values && report.test_values.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-zinc-800">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Test</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Value</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Reference</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.test_values.map((test, index) => (
                            <tr 
                              key={index} 
                              className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors"
                              data-testid={`test-value-${index}`}
                            >
                              <td className="py-3 px-4">
                                <span className="font-medium text-white">{test.test_name}</span>
                              </td>
                              <td className="py-3 px-4">
                                <span className="font-mono text-blue-300">{test.value}</span>
                                {test.unit && <span className="text-zinc-500 ml-1">{test.unit}</span>}
                              </td>
                              <td className="py-3 px-4 text-zinc-400 text-sm">
                                {test.reference_range || "-"}
                              </td>
                              <td className="py-3 px-4">
                                <Badge className={`${getStatusColor(test.status)} border`}>
                                  <span className="flex items-center gap-1">
                                    {getStatusIcon(test.status)}
                                    {test.status || "Normal"}
                                  </span>
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-zinc-500">No test values extracted</p>
                  )}
                </div>
              </CollapsibleSection>

              {/* Abnormal Findings */}
              {report.abnormal_findings && report.abnormal_findings.length > 0 && (
                <CollapsibleSection
                  icon={AlertCircle}
                  title="Abnormal Findings"
                  count={report.abnormal_findings.length}
                  isOpen={expandedSections.abnormal}
                  onToggle={() => toggleSection("abnormal")}
                  testId="abnormal-findings-section"
                  accentColor="amber"
                >
                  <div className="space-y-3">
                    {report.abnormal_findings.map((finding, index) => (
                      <div 
                        key={index} 
                        className="p-4 bg-amber-950/20 rounded-r-lg"
                        style={{ borderLeft: '3px solid #f59e0b' }}
                        data-testid={`abnormal-finding-${index}`}
                      >
                        <p className="text-amber-200">{finding}</p>
                      </div>
                    ))}
                  </div>
                </CollapsibleSection>
              )}

              {/* Full Analysis */}
              <CollapsibleSection
                icon={Activity}
                title="Full Analysis"
                isOpen={expandedSections.fullAnalysis}
                onToggle={() => toggleSection("fullAnalysis")}
                testId="full-analysis-section"
                accentColor="blue"
              >
                <div className="whitespace-pre-wrap text-zinc-300" data-testid="full-analysis">
                  {report.full_analysis || "No detailed analysis available"}
                </div>
              </CollapsibleSection>
            </div>

            {/* Right Column - Sidebar */}
            <div className="lg:col-span-4 space-y-6">
              {/* Urgent Flags */}
              {report.urgent_flags && report.urgent_flags.length > 0 && (
                <Card className="bg-zinc-900/50 border-red-900/50 overflow-hidden">
                  <CardHeader className="bg-red-950/30 border-b border-red-900/30 pb-4">
                    <CardTitle className="flex items-center gap-3 text-red-400">
                      <AlertTriangle className="w-5 h-5" />
                      Urgent Flags
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-2" data-testid="urgent-flags-list">
                    {report.urgent_flags.map((flag, index) => (
                      <div 
                        key={index} 
                        className="p-3 bg-red-950/30 rounded-r"
                        style={{ borderLeft: '2px solid #ef4444' }}
                        data-testid={`urgent-flag-${index}`}
                      >
                        <p className="text-sm text-red-200">{flag}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Possible Conditions */}
              {report.possible_conditions && report.possible_conditions.length > 0 && (
                <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
                  <CardHeader className="bg-violet-950/20 border-b border-violet-900/20 pb-4">
                    <CardTitle className="flex items-center gap-3 text-violet-400 text-base">
                      <Heart className="w-5 h-5" />
                      Possible Conditions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-2" data-testid="conditions-list">
                    {report.possible_conditions.map((condition, index) => (
                      <div 
                        key={index} 
                        className="p-3 bg-violet-950/20 rounded-r"
                        style={{ borderLeft: '2px solid #8b5cf6' }}
                        data-testid={`condition-${index}`}
                      >
                        <p className="text-sm text-violet-200">{condition}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Recommended Actions */}
              {report.recommended_actions && report.recommended_actions.length > 0 && (
                <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
                  <CardHeader className="bg-emerald-950/20 border-b border-emerald-900/20 pb-4">
                    <CardTitle className="flex items-center gap-3 text-emerald-400 text-base">
                      <Stethoscope className="w-5 h-5" />
                      Recommended Actions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-2" data-testid="actions-list">
                    {report.recommended_actions.map((action, index) => (
                      <div 
                        key={index} 
                        className="p-3 bg-emerald-950/20 rounded-r"
                        style={{ borderLeft: '2px solid #10b981' }}
                        data-testid={`action-${index}`}
                      >
                        <p className="text-sm text-emerald-200">{action}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Lifestyle Recommendations */}
              {report.lifestyle_recommendations && report.lifestyle_recommendations.length > 0 && (
                <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
                  <CardHeader className="bg-sky-950/20 border-b border-sky-900/20 pb-4">
                    <CardTitle className="flex items-center gap-3 text-sky-400 text-base">
                      <Lightbulb className="w-5 h-5" />
                      Lifestyle Tips
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-2" data-testid="lifestyle-list">
                    {report.lifestyle_recommendations.map((tip, index) => (
                      <div 
                        key={index} 
                        className="p-3 bg-sky-950/20 rounded-r"
                        style={{ borderLeft: '2px solid #0ea5e9' }}
                        data-testid={`lifestyle-${index}`}
                      >
                        <p className="text-sm text-sky-200">{tip}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Actions */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-4 space-y-3">
                  <Button
                    onClick={() => navigate("/labs/lab-reports")}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    data-testid="new-lab-analysis-btn"
                  >
                    New Lab Report
                  </Button>
                  <Button
                    onClick={() => navigate("/labs/history")}
                    variant="outline"
                    className="w-full bg-zinc-900 border-zinc-700 hover:bg-zinc-800"
                    data-testid="view-history-btn"
                  >
                    View All History
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </LabsLayout>
  );
}

const CollapsibleSection = ({ icon: Icon, title, count, isOpen, onToggle, children, testId, accentColor = "emerald" }) => {
  const colorMap = {
    emerald: "text-emerald-500",
    amber: "text-amber-500",
    blue: "text-blue-500",
    red: "text-red-500"
  };

  return (
    <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <button
            className="w-full flex items-center justify-between p-4 hover:bg-zinc-800/50 transition-colors"
            data-testid={`${testId}-toggle`}
          >
            <div className="flex items-center gap-3">
              <Icon className={`w-5 h-5 ${colorMap[accentColor]}`} />
              <h3 className="text-lg font-semibold font-manrope text-white">{title}</h3>
              {count !== undefined && (
                <Badge variant="secondary" className="bg-zinc-800 text-zinc-400">
                  {count}
                </Badge>
              )}
            </div>
            {isOpen ? (
              <ChevronUp className="w-5 h-5 text-zinc-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-zinc-400" />
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Separator className="bg-zinc-800" />
          <div className="p-4" data-testid={testId}>
            {children}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

const LoadingSkeleton = () => (
  <div className="min-h-[calc(100vh-4rem)] py-8 bg-[#09090B]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center gap-4 mb-8">
        <Skeleton className="w-10 h-10 rounded-lg bg-zinc-800" />
        <div className="space-y-2">
          <Skeleton className="w-48 h-8 bg-zinc-800" />
          <Skeleton className="w-32 h-4 bg-zinc-800" />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <Skeleton className="w-full h-40 rounded-lg bg-zinc-800" />
          <Skeleton className="w-full h-64 rounded-lg bg-zinc-800" />
        </div>
        <div className="lg:col-span-4 space-y-6">
          <Skeleton className="w-full h-48 rounded-lg bg-zinc-800" />
          <Skeleton className="w-full h-48 rounded-lg bg-zinc-800" />
        </div>
      </div>
    </div>
  </div>
);
